/**
 * Wire contracts for the /admin/entities/* endpoints (PR #5a.1).
 *
 * An entity is a counterparty CORTEX knows about — the one Insurer
 * (Lombard Insurance), a Brokerage like Aon, eventually Clients,
 * Reinsurers, Loss Adjusters, TPAs. Per `docs/USER-ENTITY-MODEL.md`
 * §2 the EntityType taxonomy is admin-configurable at runtime, so the
 * type slug is a string rather than a hard-coded enum on the wire.
 *
 * Contact and address fields are all optional. They capture the
 * minimum operational metadata needed to write a policy schedule
 * and to satisfy POPIA's "data subject contact" requirement.
 */

import { z } from 'zod';

const LifecycleStatusSchema = z.enum(['Active', 'Suspended']);

/**
 * Trimmed, non-empty text — Zod's default `min(1)` is satisfied by
 * pure whitespace, which is almost never what a CRUD form wants.
 */
const TrimmedNonEmpty = (max = 200): z.ZodString => z.string().trim().min(1, 'required').max(max);

/** Field that's either omitted, null, or a non-empty trimmed string. */
const OptionalTrimmed = (max = 200): z.ZodOptional<z.ZodNullable<z.ZodString>> =>
  z.string().trim().min(1).max(max).nullable().optional();

const OptionalEmail = (): z.ZodOptional<z.ZodNullable<z.ZodString>> =>
  z.string().trim().toLowerCase().email().max(254).nullable().optional();

/** ISO 3166-1 alpha-2 — two upper-case letters. Defaults to ZA at the DB. */
const CountryCodeSchema = z
  .string()
  .trim()
  .length(2, 'country code must be two letters (ISO 3166-1 alpha-2)')
  .regex(/^[A-Z]{2}$/u, 'country code must be upper-case')
  .optional();

export const EntitySchema = z.object({
  id: z.string().uuid(),
  entityTypeId: z.string().uuid(),
  entityTypeSlug: z.string().min(1),
  entityTypeDisplayName: z.string().min(1),

  name: z.string().min(1),
  tradingName: z.string().nullable(),
  fspNumber: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  vatNumber: z.string().nullable(),
  primaryContactName: z.string().nullable(),
  primaryContactEmail: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().min(2),

  status: LifecycleStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const EntityCreateRequestSchema = z.object({
  entityTypeSlug: TrimmedNonEmpty(64),
  name: TrimmedNonEmpty(200),
  tradingName: OptionalTrimmed(200),
  fspNumber: OptionalTrimmed(32),
  registrationNumber: OptionalTrimmed(64),
  vatNumber: OptionalTrimmed(32),
  primaryContactName: OptionalTrimmed(200),
  primaryContactEmail: OptionalEmail(),
  phoneNumber: OptionalTrimmed(32),
  addressLine1: OptionalTrimmed(200),
  addressLine2: OptionalTrimmed(200),
  city: OptionalTrimmed(120),
  postalCode: OptionalTrimmed(20),
  country: CountryCodeSchema,
  status: LifecycleStatusSchema.optional(),
});

/**
 * Update is a partial of create's surface, MINUS `entityTypeSlug` —
 * changing the type of an existing entity is intentionally disallowed
 * (would invalidate role assignments, facility scopes, audit semantics).
 */
export const EntityUpdateRequestSchema = z.object({
  name: TrimmedNonEmpty(200).optional(),
  tradingName: OptionalTrimmed(200),
  fspNumber: OptionalTrimmed(32),
  registrationNumber: OptionalTrimmed(64),
  vatNumber: OptionalTrimmed(32),
  primaryContactName: OptionalTrimmed(200),
  primaryContactEmail: OptionalEmail(),
  phoneNumber: OptionalTrimmed(32),
  addressLine1: OptionalTrimmed(200),
  addressLine2: OptionalTrimmed(200),
  city: OptionalTrimmed(120),
  postalCode: OptionalTrimmed(20),
  country: CountryCodeSchema,
  status: LifecycleStatusSchema.optional(),
});

export const EntityListQuerySchema = z.object({
  entityTypeSlug: z.string().min(1).optional(),
  status: LifecycleStatusSchema.optional(),
  search: z.string().trim().min(1).max(200).optional(),
});

export const EntityListResponseSchema = z.object({
  entities: z.array(EntitySchema),
});

export const EntityResponseSchema = z.object({
  entity: EntitySchema,
});

export type Entity = z.infer<typeof EntitySchema>;
export type EntityCreateRequest = z.infer<typeof EntityCreateRequestSchema>;
export type EntityUpdateRequest = z.infer<typeof EntityUpdateRequestSchema>;
export type EntityListQuery = z.infer<typeof EntityListQuerySchema>;
export type EntityListResponse = z.infer<typeof EntityListResponseSchema>;
export type EntityResponse = z.infer<typeof EntityResponseSchema>;
